import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { ClientError } from "graphql-request";

import { graphql } from "lib/generated/app-service-gql";
import {
  UnhideEntityMutation,
  UnhideEntityMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventNames, useTracking } from "lib/tracking";
import { HiddenEntityType } from "lib/types/graphQLEnums";
import { useGraphQlMutation } from "../useGraphQlClient";
import { NoticeVisibilityTarget } from "./useHideNotice";
import { updateCachedNoticeHidden } from "./utils";

const unhideEntityMutation = graphql(`
  mutation unhideEntity($input: HiddenEntityInput!) {
    unhideEntity(input: $input) {
      entityType
      entityId
      isHidden
    }
  }
`);

/**
 * Restores a notice the current user's team hid. Returns `unhideNotice`, which restores the given notice.
 */
export function useUnhideNotice(
  notice: NoticeVisibilityTarget,
  contextSource: string,
  options?: UseMutationOptions<UnhideEntityMutation, ClientError, UnhideEntityMutationVariables>,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();

  const mutation = useGraphQlMutation(unhideEntityMutation, {
    ...options,
    onSuccess: (data, variables, context) => {
      logEvent(EventNames.qualificationActioned, {
        "Record GUID": notice.guid,
        "Context source": contextSource,
        "Qualification label": "Unqualified",
        Stage: notice.procurementStage.stage,
        "Signal types": notice.signalTypes ? Object.keys(notice.signalTypes) : [],
        "Signal score": notice.score ?? "0",
        Signals: notice.signalTypes,
      });

      updateCachedNoticeHidden(queryClient, notice, false);

      options?.onSuccess?.(data, variables, context);
    },
  });

  return {
    ...mutation,
    unhideNotice: () =>
      mutation.mutate({
        input: { entityType: HiddenEntityType.Notice, entityId: notice.procurementStage.id },
      }),
  };
}
